import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

const KLAVIYO_ENDPOINT = 'https://a.klaviyo.com/client/back-in-stock-subscriptions/';
const KLAVIYO_REVISION = '2024-10-15';

/**
 * Reads the Klaviyo public key from the onsite script Klaviyo's app embed injects.
 * Used as a fallback when the block setting is empty.
 * @returns {string | null}
 */
function detectCompanyId() {
  const script = document.querySelector('script[src*="static.klaviyo.com/onsite/js/"]');
  const match = script?.getAttribute('src')?.match(/\/onsite\/js\/([^/]+)\/klaviyo\.js/);
  return match ? match[1] : null;
}

/**
 * Builds the request body for a Klaviyo back-in-stock subscription.
 * @param {string} email
 * @param {string} variantId - Shopify variant id.
 */
function buildPayload(email, variantId) {
  return {
    data: {
      type: 'back-in-stock-subscription',
      attributes: {
        channels: ['EMAIL'],
        profile: { data: { type: 'profile', attributes: { email } } },
      },
      relationships: {
        variant: { data: { type: 'catalog-variant', id: `$shopify:::$default:::${variantId}` } },
      },
    },
  };
}

class BackInStockForm extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.handleSubmit);
    this.closestSection?.addEventListener(ThemeEvents.variantUpdate, this.updateVariant);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.handleSubmit);
    this.closestSection?.removeEventListener(ThemeEvents.variantUpdate, this.updateVariant);
  }

  get closestSection() {
    return this.closest('.shopify-section, dialog');
  }

  /**
   * Swaps the form for the re-rendered one when the selected variant changes.
   * @param {CustomEvent} event
   */
  updateVariant = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const updated = event.detail.data.html.querySelector('back-in-stock-form');
    if (!updated) return;

    morph(this, updated, { childrenOnly: true });
  };

  /**
   * @param {SubmitEvent} event
   */
  handleSubmit = async (event) => {
    event.preventDefault();
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const input = form.querySelector('input[type="email"]');
    const button = form.querySelector('button[type="submit"]');
    if (!(input instanceof HTMLInputElement) || !(button instanceof HTMLButtonElement)) return;

    if (!input.checkValidity()) {
      input.reportValidity();
      return;
    }

    const companyId = this.dataset.companyId || detectCompanyId();
    const variantId = form.dataset.variantId;

    if (!companyId || !variantId) {
      console.error('[back-in-stock] Missing Klaviyo public key or variant id');
      this.setState(form, 'error');
      return;
    }

    button.disabled = true;
    this.setState(form, 'pending');

    try {
      const response = await fetch(`${KLAVIYO_ENDPOINT}?company_id=${encodeURIComponent(companyId)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: KLAVIYO_REVISION },
        body: JSON.stringify(buildPayload(input.value.trim(), variantId)),
      });

      if (!response.ok) {
        throw new Error(`Klaviyo responded with ${response.status}`);
      }

      this.setState(form, 'success');
    } catch (error) {
      console.error('[back-in-stock] Subscription failed', error);
      this.setState(form, 'error');
    } finally {
      button.disabled = false;
    }
  };

  /**
   * @param {HTMLFormElement} form
   * @param {'pending' | 'success' | 'error'} state
   */
  setState(form, state) {
    form.dataset.state = state;
    form.querySelectorAll('.back-in-stock__message').forEach((message) => {
      if (!(message instanceof HTMLElement)) return;
      message.hidden = !message.classList.contains(`back-in-stock__message--${state}`);
    });
  }
}

if (!customElements.get('back-in-stock-form')) {
  customElements.define('back-in-stock-form', BackInStockForm);
}
